import { FC, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DropzonePresenter } from './DropzonePresenter';
import type { DropzoneProps } from './types';

/**
 * Область загрузки файлов: принимает перетаскивание и выбор, отдаёт прошедшие проверку файлы
 * и причины отказа по остальным.
 */
export const Dropzone: FC<DropzoneProps> = ({
  acceptedFileTypes = ['.csv', '.xlsx', '.xls'],
  maxFileSize = 10,
  maxFiles = 5,
  currentFilesCount = 0,
  onFilesChange,
  onErrorsChange,
  className,
  disabled = false,
  size = 'default',
}) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const { t } = useTranslation(['records']);

  const isDisabled = disabled || currentFilesCount >= maxFiles;

  // Недоступная область не должна остаться подсвеченной перетаскиванием, начатым до блокировки
  useEffect(() => {
    if (isDisabled) {
      setIsDragOver(false);
    }
  }, [isDisabled]);

  const validateFile = useCallback(
    (file: File): string | null => {
      const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
      if (!acceptedFileTypes.includes(fileExtension)) {
        return t('records:fileUpload.invalidFileType', { types: acceptedFileTypes.join(', ') });
      }

      const fileSizeMB = file.size / (1024 * 1024);
      if (fileSizeMB > maxFileSize) {
        return t('records:fileUpload.fileTooLarge', { maxSize: maxFileSize });
      }

      return null;
    },
    [acceptedFileTypes, maxFileSize, t],
  );

  const handleFiles = useCallback(
    (files: FileList | File[]) => {
      const fileArray = Array.from(files);
      const newErrors: string[] = [];
      const validFiles: File[] = [];

      // Выбор сверх предела отклоняется целиком, а не усекается
      if (fileArray.length > maxFiles) {
        const error = t('records:fileUpload.maxFilesExceeded', { maxFiles });
        newErrors.push(error);
        onErrorsChange?.(newErrors);
        return;
      }

      fileArray.forEach((file) => {
        const error = validateFile(file);
        if (error) {
          newErrors.push(`${file.name}: ${error}`);
        } else {
          validFiles.push(file);
        }
      });

      if (newErrors.length > 0) {
        onErrorsChange?.(newErrors);
      } else {
        onErrorsChange?.([]);
      }

      if (validFiles.length > 0) {
        onFilesChange(validFiles);
      }
    },
    [maxFiles, validateFile, onFilesChange, onErrorsChange, t],
  );

  const handleDragOver = useCallback(
    (e: React.DragEvent) => {
      if (isDisabled) return;
      e.preventDefault();
      setIsDragOver(true);
    },
    [isDisabled],
  );

  const handleDragLeave = useCallback(
    (e: React.DragEvent) => {
      if (isDisabled) return;
      e.preventDefault();
      // Переход курсора на вложенный элемент области — не уход с неё
      if (!e.currentTarget.contains(e.relatedTarget as Node)) {
        setIsDragOver(false);
      }
    },
    [isDisabled],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      if (isDisabled) return;
      e.preventDefault();
      setIsDragOver(false);
      const files = e.dataTransfer.files;
      handleFiles(files);
    },
    [handleFiles, isDisabled],
  );

  const handleFileInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (isDisabled) return;
      if (e.target.files) {
        handleFiles(e.target.files);
      }
    },
    [handleFiles, isDisabled],
  );

  return (
    <DropzonePresenter
      isDragOver={isDragOver}
      isDisabled={isDisabled}
      acceptedFileTypes={acceptedFileTypes}
      maxFileSize={maxFileSize}
      maxFiles={maxFiles}
      size={size}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onFileInput={handleFileInput}
      className={className}
    />
  );
};
